/**
 * PwaInstallPrompt — Captures `beforeinstallprompt`, registers the SW, and
 * surfaces an install banner the first time the user lands on an
 * authenticated page.
 *
 * UX rules
 * ────────
 *   • Never shown to users running standalone (already installed).
 *   • Auto-dismiss persists for 7 days via localStorage.
 *   • iOS Safari has no `beforeinstallprompt` → we show an instruction
 *     banner ("Compartir → Añadir a pantalla de inicio").
 */
import { useEffect, useState } from 'react';
import { Download, X, Share, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'aircom_pwa_dismissed_at';
const DISMISS_DAYS = 1;  // Re-prompt daily — installation is the #1 UX win.

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

const recentlyDismissed = () => {
  const t = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
  return t && (Date.now() - t) < DISMISS_DAYS * 24 * 3600 * 1000;
};

const registerSW = () => {
  if (!('serviceWorker' in navigator)) return;
  // Register asynchronously so it never blocks first paint.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => null);
  });
};

const PwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    registerSW();
    if (isStandalone() || recentlyDismissed()) return undefined;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onBefore);

    // iOS Safari path: show instruction banner after 4s if no event fired.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShowIOS(true), 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setShowIOS(false);
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);
    setShow(false);
    if (choice?.outcome !== 'accepted') {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  };

  if (!show && !showIOS) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3" data-testid="pwa-install-prompt">
      <div className="mx-auto max-w-md rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-white/10 p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center shrink-0">
          {showIOS ? <Smartphone className="w-5 h-5 text-teal-300" /> : <Download className="w-5 h-5 text-teal-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Instalá Aircom como app</p>
          {showIOS ? (
            <p className="text-xs text-slate-300 mt-1 leading-snug">
              Tocá <Share className="w-3 h-3 inline -mt-0.5" /> <b>Compartir</b> y elegí
              <b> "Añadir a pantalla de inicio"</b>. Te llegan notificaciones al instante.
            </p>
          ) : (
            <p className="text-xs text-slate-300 mt-1 leading-snug">
              Acceso directo, modo pantalla completa y notificaciones push. Todo en 2 segundos.
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            {!showIOS && (
              <button
                onClick={install}
                className="px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 text-slate-900 text-xs font-semibold"
                data-testid="pwa-install-btn"
              >
                Instalar ahora
              </button>
            )}
            <button
              onClick={dismiss}
              className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-xs text-slate-200"
              data-testid="pwa-dismiss-btn"
            >
              {showIOS ? 'Entendido' : 'Más tarde'}
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="text-slate-400 hover:text-white p-1" data-testid="pwa-close">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
